import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { Button, Col, Container, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Circle, Group, Line, Rect, Shape, Text } from "react-konva";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import VideoPlayer from "@/components/VideoPlayer";
import FieldSubmitForm from "./FieldSubmitForm";
import Preloader from "@/components/Elements/Preloader";
import { FieldMarkingHintsConstant, Stages } from "./constants";
import { markupApi } from "@/api";

import "../../scss/components/_field.scss";

const DEFAULT_FPS = 25;
const DEFAULT_WIDTH = 1030;
const DEFAULT_RADIUS = 8;

const saveCalibrationToState = (v) => (v !== null ? v : "");
const saveCalibrationToSubmit = (v) => (v !== "" ? +v : null);

const DEFAULT_FIELD_POINTS = [
  { id: "0", x: 0.1, y: 0.25, color: "#6200EE", desc: "Top Left" },
  { id: "1", x: 0.5, y: 0.25, color: "#03DAc6", desc: "Top Middle" },
  { id: "2", x: 0.9, y: 0.25, color: "#B00020", desc: "Top Right" },
  { id: "3", x: 0.9, y: 0.75, color: "#FFDE03", desc: "Bottom Right" },
  { id: "4", x: 0.5, y: 0.75, color: "#61D800", desc: "Bottom Middle" },
  { id: "5", x: 0.1, y: 0.75, color: "#90CAF9", desc: "Bottom Left" },
  { id: "6", x: 0.1, y: 0.5, color: "#90CAF9", desc: "Middle Left" },
  { id: "7", x: 0.5, y: 0.5, color: "#90CAF9", desc: "Middle" },
  { id: "8", x: 0.9, y: 0.5, color: "#90CAF9", desc: "Middle Right" },
];

const DEFAULT_ZONE_POINTS = [
  { id: "penalty_left_0", x: 0.05, y: 0.30, color: "#FF6B6B", desc: "Штрафная лев. ↖", group: "penalty_left" },
  { id: "penalty_left_1", x: 0.20, y: 0.30, color: "#FF6B6B", desc: "Штрафная лев. ↗", group: "penalty_left" },
  { id: "penalty_left_2", x: 0.20, y: 0.70, color: "#FF6B6B", desc: "Штрафная лев. ↘", group: "penalty_left" },
  { id: "penalty_left_3", x: 0.05, y: 0.70, color: "#FF6B6B", desc: "Штрафная лев. ↙", group: "penalty_left" },
  { id: "penalty_right_0", x: 0.80, y: 0.30, color: "#FF6B6B", desc: "Штрафная прав. ↖", group: "penalty_right" },
  { id: "penalty_right_1", x: 0.95, y: 0.30, color: "#FF6B6B", desc: "Штрафная прав. ↗", group: "penalty_right" },
  { id: "penalty_right_2", x: 0.95, y: 0.70, color: "#FF6B6B", desc: "Штрафная прав. ↘", group: "penalty_right" },
  { id: "penalty_right_3", x: 0.80, y: 0.70, color: "#FF6B6B", desc: "Штрафная прав. ↙", group: "penalty_right" },
  { id: "goal_area_left_0", x: 0.05, y: 0.38, color: "#4FC3F7", desc: "Вратарская лев. ↖", group: "goal_area_left" },
  { id: "goal_area_left_1", x: 0.10, y: 0.38, color: "#4FC3F7", desc: "Вратарская лев. ↗", group: "goal_area_left" },
  { id: "goal_area_left_2", x: 0.10, y: 0.62, color: "#4FC3F7", desc: "Вратарская лев. ↘", group: "goal_area_left" },
  { id: "goal_area_left_3", x: 0.05, y: 0.62, color: "#4FC3F7", desc: "Вратарская лев. ↙", group: "goal_area_left" },
  { id: "goal_area_right_0", x: 0.90, y: 0.38, color: "#4FC3F7", desc: "Вратарская прав. ↖", group: "goal_area_right" },
  { id: "goal_area_right_1", x: 0.95, y: 0.38, color: "#4FC3F7", desc: "Вратарская прав. ↗", group: "goal_area_right" },
  { id: "goal_area_right_2", x: 0.95, y: 0.62, color: "#4FC3F7", desc: "Вратарская прав. ↘", group: "goal_area_right" },
  { id: "goal_area_right_3", x: 0.90, y: 0.62, color: "#4FC3F7", desc: "Вратарская прав. ↙", group: "goal_area_right" },
  { id: "goalpost_left_0", x: 0.05, y: 0.43, color: "#FFD700", desc: "Штанга лев. верх", group: "goalpost_left" },
  { id: "goalpost_left_1", x: 0.05, y: 0.57, color: "#FFD700", desc: "Штанга лев. низ", group: "goalpost_left" },
  { id: "goalpost_right_0", x: 0.95, y: 0.43, color: "#FFD700", desc: "Штанга прав. верх", group: "goalpost_right" },
  { id: "goalpost_right_1", x: 0.95, y: 0.57, color: "#FFD700", desc: "Штанга прав. низ", group: "goalpost_right" },
];

function makePoints(defs) {
  return defs.map((p) => ({ ...p, isDragging: false }));
}

function restorePoints(saved, defaults) {
  const result = [];
  for (const src of saved) {
    const def = defaults.find((d) => d.id === src.id);
    if (def) result.push({ ...def, ...src, isDragging: false });
  }
  for (const def of defaults) {
    if (!result.find((p) => p.id === def.id)) {
      result.push({ ...def, isDragging: false });
    }
  }
  return result;
}

export default function FieldPage() {
  const { matchId } = useParams();
  const { user } = useSelector(({ auth }) => auth);
  const isAdmin = !!user?.is_staff;
  const videoRef = useRef();
  const formRef = useRef();

  const [videoSource, setVideoSource] = useState(null);
  const [calibration, setCalibration] = useState("");
  const [field, setField] = useState({ width: 0, height: 0 });

  const [points, setPoints] = useState(() => makePoints(DEFAULT_FIELD_POINTS));
  const [zonePoints, setZonePoints] = useState(() => makePoints(DEFAULT_ZONE_POINTS));
  const [active, setActive] = useState(null);

  const [firstStageEditor, setFirstStageEditor] = useState(false);
  const [secondStageEditor, setSecondStageEditor] = useState(false);

  const [videoDomWidth, setVideoDomWidth] = useState(0);
  const [videoDomHeight, setVideoDomHeight] = useState(0);
  const [stageScale, setStageScale] = useState(1);

  const [showFrame, setShowFrame] = useState(false);
  const [frame, setFrame] = useState(null);
  const [pendingFrame, setPendingFrame] = useState(false);
  const [frameError, setFrameError] = useState(null);

  // ── Data loading ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    markupApi.getFieldMeta(matchId).then((res) => {
      if (cancelled) return;
      const { field: f, source, calibration: cal } = res;
      setVideoSource(source);
      setCalibration(saveCalibrationToState(cal));
      setField(f);
      if (f.points?.length > 0) {
        setPoints(restorePoints(f.points, DEFAULT_FIELD_POINTS));
      }
      if (f.zone_points?.length > 0) {
        setZonePoints(restorePoints(f.zone_points, DEFAULT_ZONE_POINTS));
      }
      setTimeout(() => {
        formRef.current?.onValuesUpdate({
          width: f.width,
          height: f.height,
          penaltyAreaLength: f.penalty_area_length ? String(f.penalty_area_length) : "",
          penaltyAreaWidth: f.penalty_area_width ? String(f.penalty_area_width) : "",
          goalAreaLength: f.goal_area_length ? String(f.goal_area_length) : "",
          goalAreaWidth: f.goal_area_width ? String(f.goal_area_width) : "",
          goalWidth: f.goal_width ? String(f.goal_width) : "",
        });
      }, 0);
    });
    return () => { cancelled = true; };
  }, [matchId]);

  // ── Keyboard ──────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setActive(null);
    };
    document.addEventListener("keyup", handler);
    return () => document.removeEventListener("keyup", handler);
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────
  const canvasActivated = firstStageEditor || secondStageEditor;
  const isZoneMode = secondStageEditor;
  const w = videoDomWidth;
  const h = videoDomHeight;

  const actualRadius = w ? (w / DEFAULT_WIDTH) * DEFAULT_RADIUS : DEFAULT_RADIUS;
  const fontSize = 14 / stageScale;
  const zoneFontSize = 10 / stageScale;
  const lineWidth = 1 / stageScale;
  const hitWidth = 20 / stageScale;

  const toAbsX = useCallback((x) => x * w, [w]);
  const toAbsY = useCallback((y) => y * h, [h]);

  // ── Point handlers ────────────────────────────────────────────────────
  const handlePointDragStart = useCallback((e) => {
    const id = e.target.id();
    setPoints((prev) => prev.map((p) => ({ ...p, isDragging: p.id === id })));
    setActive(id);
  }, []);

  const handlePointDragMove = useCallback(
    (e) => {
      if (!w || !h) return;
      const id = e.target.id();
      e.target.x(0);
      e.target.y(0);
      const pos = e.target.getRelativePointerPosition();
      setPoints((prev) =>
        prev.map((p) => (p.id === id ? { ...p, x: pos.x / w, y: pos.y / h } : p))
      );
    },
    [w, h]
  );

  const handlePointDragEnd = useCallback((e) => {
    const id = e.target.id();
    setPoints((prev) => prev.map((p) => (p.id === id ? { ...p, isDragging: false } : p)));
  }, []);

  const handlePointClick = useCallback((e) => {
    setActive(e.target.id());
  }, []);

  // ── Zone point handlers ───────────────────────────────────────────────
  const handleZonePointDragStart = useCallback((e) => {
    const id = e.target.id();
    setZonePoints((prev) => prev.map((p) => ({ ...p, isDragging: p.id === id })));
    setActive(id);
  }, []);

  const handleZonePointDragMove = useCallback(
    (e) => {
      if (!w || !h) return;
      const id = e.target.id();
      e.target.x(0);
      e.target.y(0);
      const pos = e.target.getRelativePointerPosition();
      setZonePoints((prev) =>
        prev.map((p) => (p.id === id ? { ...p, x: pos.x / w, y: pos.y / h } : p))
      );
    },
    [w, h]
  );

  const handleZonePointDragEnd = useCallback((e) => {
    const id = e.target.id();
    setZonePoints((prev) => prev.map((p) => (p.id === id ? { ...p, isDragging: false } : p)));
  }, []);

  const handleZonePointClick = useCallback((e) => {
    setActive(e.target.id());
  }, []);

  // ── Mode / orientation ────────────────────────────────────────────────
  const handleModeChange = useCallback((stage) => {
    if (stage === Stages.FIRST) setFirstStageEditor((v) => !v);
    else setSecondStageEditor((v) => !v);
  }, []);

  const handleSwitchX = useCallback(() => {
    setPoints((prev) => prev.map((p) => ({ ...p, x: +(1 - p.x).toFixed(2) })));
  }, []);

  const handleSwitchY = useCallback(() => {
    setPoints((prev) => prev.map((p) => ({ ...p, y: +(1 - p.y).toFixed(2) })));
  }, []);

  const resetFieldPoints = useCallback(() => {
    setPoints(makePoints(DEFAULT_FIELD_POINTS));
    setActive(null);
  }, []);

  const resetZonePointsFn = useCallback(() => {
    setZonePoints(makePoints(DEFAULT_ZONE_POINTS));
  }, []);

  const secondStageDisabled = useMemo(
    () => firstStageEditor || points.length < 4 || !field.height || !field.width,
    [firstStageEditor, points.length, field.height, field.width]
  );

  // ── Form / submit ─────────────────────────────────────────────────────
  const onChange = useCallback((values) => {
    setField((prev) => ({
      ...prev,
      height: values.height,
      width: values.width,
      penaltyAreaLength: values.penaltyAreaLength,
      penaltyAreaWidth: values.penaltyAreaWidth,
      goalAreaLength: values.goalAreaLength,
      goalAreaWidth: values.goalAreaWidth,
      goalWidth: values.goalWidth,
      zoneEnabled: values.zoneEnabled || prev.zoneEnabled,
    }));
  }, []);

  const onSubmit = useCallback(async () => {
    const fieldPoints = points.map((p) => ({ id: p.id, x: p.x, y: p.y }));
    if (![4, 6, 9].includes(fieldPoints.length)) {
      alert("The number of points on the field must be one of 9, 6 or 4!");
      return;
    }
    const zps = zonePoints.map((p) => ({ id: p.id, x: p.x, y: p.y }));
    const toInt = (v) => {
      const n = parseInt(v, 10);
      return isNaN(n) || n <= 0 ? null : n;
    };
    const ze = field.zoneEnabled || {};
    const body = {
      width: field.width,
      height: field.height,
      points: fieldPoints,
      zone_points: zps,
      penalty_area_length: ze.penalty ? toInt(field.penaltyAreaLength) : null,
      penalty_area_width: ze.penalty ? toInt(field.penaltyAreaWidth) : null,
      goal_area_length: ze.goalArea ? toInt(field.goalAreaLength) : null,
      goal_area_width: ze.goalArea ? toInt(field.goalAreaWidth) : null,
      goal_width: ze.goalposts ? toInt(field.goalWidth) : null,
      calibration: saveCalibrationToSubmit(calibration),
    };
    try {
      await markupApi.updateFieldMeta(matchId, body);
      alert("The form is filled out correctly. Results saved");
    } catch (error) {
      alert(`The form has been filled out incorrectly. Error result: ${error}`);
    }
  }, [matchId, points, zonePoints, field, calibration]);

  // ── Calibration frame ─────────────────────────────────────────────────
  const handleShowFrame = useCallback(() => {
    if (showFrame) {
      setShowFrame(false);
      setPendingFrame(false);
      return;
    }
    const currentTime = (videoRef.current?.currentTime * 100).toFixed(0);
    setShowFrame(true);
    setPendingFrame(true);
    markupApi
      .getCalibrationFrame(matchId, calibration, currentTime)
      .then((res) => {
        const data = Buffer.from(res.data, "binary").toString("base64");
        const src = `data:${res.headers["content-type"].toLowerCase()};base64,${data}`;
        setFrame(src);
        setPendingFrame(false);
        setFrameError(null);
      })
      .catch(() => {
        setFrameError("Loading error");
        setPendingFrame(false);
        setShowFrame(false);
      });
  }, [showFrame, matchId, calibration]);

  // ── VideoPlayer callbacks ─────────────────────────────────────────────
  const onVideoDomSizeChange = useCallback((width, height) => {
    setVideoDomWidth(width);
    setVideoDomHeight(height);
  }, []);

  const onStageScaleChangeCb = useCallback((scale) => {
    setStageScale(scale);
  }, []);

  const onStageMouseDown = useCallback(
    (e) => {
      const target = e.target;
      const isBackground =
        target === target.getStage() ||
        target.getClassName() === "Image" ||
        target.attrs.name === "field-bg";
      if (isBackground) setActive(null);
    },
    []
  );

  // ── Shape data for rendering ──────────────────────────────────────────
  const shapePoints = useMemo(() => {
    if (!w || !h || points.length === 0) return [];
    const ordered =
      points.length !== 9
        ? points
        : [
            points[7], points[1], points[0], points[6],
            points[7], points[6], points[5], points[4],
            points[7], points[4], points[3], points[8],
            points[7], points[8], points[2], points[1],
            points[7],
          ];
    return ordered.flatMap((p) => [p.x * w, p.y * h]);
  }, [points, w, h]);

  const generateCustomShape = useCallback(
    (context, shape) => {
      if (!w || !h || points.length === 0) return;
      context.beginPath();
      let pts = [...points];
      context.moveTo(pts[0].x * w, pts[0].y * h);
      if (pts.length === 9) {
        pts = [pts[0], pts[1], pts[2], pts[8], ...pts.slice(3, 7)];
      }
      for (const pt of pts) {
        context.lineTo(pt.x * w, pt.y * h);
      }
      context.closePath();
      context.fillStrokeShape(shape);
    },
    [points, w, h]
  );

  const zoneGroups = useMemo(() => {
    if (!isZoneMode) return {};
    const groups = {};
    for (const zp of zonePoints) {
      const def = DEFAULT_ZONE_POINTS.find((d) => d.id === zp.id);
      const group = def?.group;
      if (group) {
        if (!groups[group]) groups[group] = [];
        groups[group].push(zp);
      }
    }
    return groups;
  }, [isZoneMode, zonePoints]);

  const activePoint = useMemo(
    () =>
      active &&
      (points.find((p) => p.id === active) || zonePoints.find((p) => p.id === active)),
    [active, points, zonePoints]
  );

  const pointOptions = useMemo(
    () =>
      firstStageEditor
        ? points.map((p) => (
            <option key={p.id} value={p.id}>
              {p.desc} [{p.x.toFixed(2)} : {p.y.toFixed(2)}]
            </option>
          ))
        : null,
    [firstStageEditor, points]
  );

  // ── Render ────────────────────────────────────────────────────────────
  if (!videoSource) return <Preloader />;

  return (
    <Container className="p-3" fluid>
      <Row>
        <Col>
          <div className="viewer-wrapper" style={{ position: "relative" }}>
            <VideoPlayer
              ref={videoRef}
              src={videoSource}
              fps={DEFAULT_FPS}
              zoomButtonsVisible
              wheelZoomNeedsCtrl={false}
              aspectRatio="16 / 9"
              onVideoDomSizeChange={onVideoDomSizeChange}
              onStageScaleChange={onStageScaleChangeCb}
              onMouseDownHandler={onStageMouseDown}
            >
              {w > 0 && h > 0 && (
                <>
                  <Rect
                    name="field-bg"
                    width={w}
                    height={h}
                    fill="#000000"
                    opacity={canvasActivated ? 0.15 : 0.5}
                  />
                  <Line
                    points={shapePoints}
                    stroke="white"
                    strokeWidth={lineWidth}
                    tension={0}
                  />
                  <Shape
                    sceneFunc={generateCustomShape}
                    fill="#00D2FF"
                    opacity={0.25}
                  />
                  {points.map((point) => (
                    <Group
                      key={`fp.${point.id}`}
                      id={point.id}
                      draggable={canvasActivated}
                      onDragStart={handlePointDragStart}
                      onDragMove={handlePointDragMove}
                      onDragEnd={handlePointDragEnd}
                      onClick={handlePointClick}
                    >
                      <Circle
                        id={point.id}
                        x={toAbsX(point.x)}
                        y={toAbsY(point.y)}
                        radius={actualRadius / (3 * stageScale)}
                        hitStrokeWidth={hitWidth}
                        fill={active === point.id ? "white" : point.color}
                        shadowColor="white"
                        shadowOffset={{ x: 1, y: 1 }}
                        shadowBlur={10}
                        shadowOpacity={active === point.id ? 1 : 0}
                      />
                      <Text
                        x={toAbsX(point.x) - actualRadius * 2 / stageScale - 15 / stageScale}
                        y={toAbsY(point.y) + actualRadius * 2 / stageScale}
                        fill="#ffffff"
                        text={point.desc}
                        fontSize={fontSize}
                        opacity={canvasActivated ? 1.0 : 0.0}
                      />
                    </Group>
                  ))}

                  {isZoneMode &&
                    Object.entries(zoneGroups).map(([group, pts]) => {
                      if (pts.length < 2) return null;
                      const color = pts[0].color || "#FF6B6B";
                      const lp = pts.flatMap((p) => [toAbsX(p.x), toAbsY(p.y)]);
                      if (pts.length >= 4) lp.push(toAbsX(pts[0].x), toAbsY(pts[0].y));
                      return (
                        <Line
                          key={`zl-${group}`}
                          points={lp}
                          stroke={color}
                          strokeWidth={lineWidth * 1.5}
                          dash={[4 / stageScale, 4 / stageScale]}
                          opacity={0.7}
                        />
                      );
                    })}

                  {isZoneMode &&
                    zonePoints.map((point) => (
                      <Group
                        key={`zp.${point.id}`}
                        id={point.id}
                        draggable
                        onDragStart={handleZonePointDragStart}
                        onDragMove={handleZonePointDragMove}
                        onDragEnd={handleZonePointDragEnd}
                        onClick={handleZonePointClick}
                      >
                        <Circle
                          id={point.id}
                          x={toAbsX(point.x)}
                          y={toAbsY(point.y)}
                          radius={actualRadius / (2.5 * stageScale)}
                          hitStrokeWidth={hitWidth}
                          fill={active === point.id ? "white" : point.color}
                          stroke="white"
                          strokeWidth={lineWidth * 0.5}
                          shadowColor="white"
                          shadowBlur={active === point.id ? 10 : 0}
                          shadowOpacity={active === point.id ? 1 : 0}
                        />
                        <Text
                          x={toAbsX(point.x) - actualRadius * 2 / stageScale - 15 / stageScale}
                          y={toAbsY(point.y) + actualRadius * 2 / stageScale}
                          fill="#ffffff"
                          text={point.desc}
                          fontSize={zoneFontSize}
                          opacity={canvasActivated ? 0.8 : 0.0}
                        />
                      </Group>
                    ))}

                  {activePoint && (
                    <Text
                      x={toAbsX(0.01)}
                      y={toAbsY(0.025)}
                      text={`Active point: ${activePoint.desc}`}
                      fill="white"
                      fontSize={fontSize}
                    />
                  )}
                </>
              )}
            </VideoPlayer>

            {showFrame && !pendingFrame && frame && (
              <img
                src={frame}
                alt="calibration frame"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  pointerEvents: "none",
                  zIndex: 5,
                }}
              />
            )}

            <OverlayTrigger
              placement="top"
              delay={{ hide: 200 }}
              overlay={(props) => (
                <Tooltip id="field-markup-switch-tooltip-y" {...props}>
                  Change orientation
                </Tooltip>
              )}
            >
              <Button
                className="field-markup-switch-y"
                variant="primary"
                size="sm"
                onClick={handleSwitchY}
              >
                <FontAwesomeIcon icon={["fas", "exchange-alt"]} />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              delay={{ hide: 200 }}
              overlay={(props) => (
                <Tooltip id="field-markup-switch-tooltip-x" {...props}>
                  Change orientation
                </Tooltip>
              )}
            >
              <Button
                className="field-markup-switch-x"
                variant="success"
                size="sm"
                onClick={handleSwitchX}
              >
                <FontAwesomeIcon icon={["fas", "exchange-alt"]} />
              </Button>
            </OverlayTrigger>
          </div>
        </Col>
      </Row>

      <Row>
        <Col className="d-flex flex-column">
          <Button
            variant="outline-dark"
            className="viewer__button my-2"
            disabled={secondStageEditor}
            onClick={() => handleModeChange(Stages.FIRST)}
          >
            Field marking:{" "}
            {firstStageEditor ? "Редактирование ✏️" : "Просмотр 🔍"}
          </Button>
          <FieldSubmitForm
            ref={formRef}
            width={field.width}
            height={field.height}
            isZoneStage={secondStageEditor}
            onChange={onChange}
            onSubmit={onSubmit}
          />
          <Button
            variant="outline-danger"
            className="viewer__button"
            onClick={resetFieldPoints}
          >
            Clear
          </Button>
        </Col>

        {isAdmin && (
          <Col className="d-flex flex-column">
            <Button
              variant="outline-dark"
              className="viewer__button my-2"
              onClick={() => handleModeChange(Stages.SECOND)}
              disabled={secondStageDisabled}
            >
              Zone calibration:{" "}
              {secondStageEditor ? "Редактирование ✏️" : "Просмотр 🔍"}
            </Button>
            <Button
              variant="outline-danger"
              className="viewer__button"
              onClick={resetZonePointsFn}
              disabled={!secondStageEditor}
            >
              Clear zones
            </Button>
          </Col>
        )}

        <Col>
          <Button
            type="submit"
            variant="outline-primary"
            className="form__form_button confirm-data my-2 w-100"
            disabled={!field.width || !field.height}
            onClick={() => formRef.current?.submit()}
          >
            Send 🚀
          </Button>
          <select
            value={active || -1}
            className="form-select my-2 w-100"
            onChange={(e) => setActive(e.target.value)}
            disabled={!firstStageEditor}
          >
            <option value={-1} disabled hidden>
              Select a point on the field
            </option>
            {pointOptions}
          </select>
          <div className="flex-column">
            <div className="d-flex">
              <Button
                variant={showFrame ? "secondary" : "primary"}
                disabled={calibration === ""}
                onClick={handleShowFrame}
              >
                {pendingFrame ? (
                  <FontAwesomeIcon spin icon={["fas", "spinner"]} />
                ) : (
                  <FontAwesomeIcon
                    icon={["fas", showFrame ? "eye-slash" : "eye"]}
                  />
                )}
              </Button>
            </div>
            {frameError && (
              <div className="alert alert-warning" role="alert">
                {frameError}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}
